import React, { useState, useEffect } from 'react';
import {
  Buildings, TreeStructure, Ticket, FileXls, Info, WarningCircle, ArrowClockwise, Trash, X,
  Tray, CheckSquare, Square,
} from '@phosphor-icons/react';
import {
  getBranchNames, getGroupCode, getTickets, getReceivedDocument,
  saveData, deleteKgmsMvoSvoDetails,
} from '../api/connectionRegistryApi';
import SearchableSelect from './SearchableSelect';
import { useLoginName } from '../utils/auditUser';
import Toast from './Toast';
import { useToast } from '../utils/useToast';
import { NO_DATA_MESSAGE, NO_DATA_SUMMARY, isEmptyResult } from '../utils/noData';

// Normalizes both resolved and thrown responses to detect a plain-text "Success" reply.
function isSuccessResponse(res) {
  const text = typeof res === 'string'
    ? res
    : res?.error?.text ?? res?.error ?? res?.data ?? res?.text ?? '';
  return String(text).trim().toLowerCase() === 'success';
}

function ReceivedDocumentNotShowForm() {
  const loginName = useLoginName();
  const [formKey, setFormKey] = useState(0);

  const [branchList, setBranchList] = useState([]);
  const [groupList, setGroupList] = useState([]);
  const [ticketList, setTicketList] = useState([]);
  const [receivedDocuments, setReceivedDocuments] = useState([]);

  const [cao, setCao] = useState('');
  const [branchSchema, setBranchSchema] = useState(null);
  const [, setBranchCode] = useState(null);

  const [group, setGroup] = useState('');
  const [groupCode, setGroupCode] = useState(null);
  const [chitgroupId, setChitgroupId] = useState(null);

  const [ticket, setTicket] = useState('');
  const [ticketNo, setTicketNo] = useState(null);

  const [fileDownloadStatus, setFileDownloadStatus] = useState(false);
  const [reason, setReason] = useState('');

  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const { toast, setToast, showToast } = useToast();

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingAudit, setPendingAudit] = useState(null);
  const [pendingFileDownloadStatus, setPendingFileDownloadStatus] = useState(false);
  const [pendingReason, setPendingReason] = useState('');

  useEffect(() => {
    getBranchNames().then((res) => setBranchList(res || [])).catch(() => setBranchList([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formKey]);

  const preventExceedLength = (event, max) => {
    const input = event.target;
    if (
      input.value.length >= max &&
      event.key !== 'Backspace' &&
      event.key !== 'Delete' &&
      event.key !== 'ArrowLeft' &&
      event.key !== 'ArrowRight' &&
      event.key !== 'Tab'
    ) {
      event.preventDefault();
    }
  };

  const resetFromTicket = () => {
    setTicketNo(null);
    setReceivedDocuments([]);
    setFileDownloadStatus(false);
    setReason('');
  };

  const resetFromGroup = () => {
    setGroupCode(null);
    setChitgroupId(null);
    setTicket('');
    setTicketList([]);
    resetFromTicket();
  };

  const resetFromBranch = () => {
    setBranchSchema(null);
    setBranchCode(null);
    setGroup('');
    setGroupList([]);
    resetFromGroup();
  };

  const handleBranchChange = async (branchName) => {
    setCao(branchName);
    resetFromBranch();
    if (!branchName) return;

    const selected = branchList.find((b) => b.branch_name === branchName);
    if (!selected) return;

    const schema = selected.branch_schema ?? selected.branch_code;
    setBranchSchema(schema);
    setBranchCode(selected.branch_code);

    try {
      const res = await getGroupCode(schema);
      setGroupList(res || []);
      if (isEmptyResult(res)) showToast('warn', NO_DATA_SUMMARY, NO_DATA_MESSAGE);
    } catch {
      setGroupList([]);
    }
  };

  const handleGroupChange = async (groupCodeValue) => {
    setGroup(groupCodeValue);
    resetFromGroup();
    if (!groupCodeValue) return;

    const selected = groupList.find((g) => String(g.group_Code) === String(groupCodeValue));
    if (!selected) return;

    setGroupCode(selected.group_Code);
    setChitgroupId(selected.chitgroup_id);

    try {
      const res = await getTickets(branchSchema, selected.group_Code);
      setTicketList(res || []);
      if (isEmptyResult(res)) showToast('warn', NO_DATA_SUMMARY, NO_DATA_MESSAGE);
    } catch {
      setTicketList([]);
    }
  };

  const handleTicketChange = async (ticketNoValue) => {
    setTicket(ticketNoValue);
    resetFromTicket();
    if (!ticketNoValue) return;

    const selected = ticketList.find((t) => String(t.ticketno) === String(ticketNoValue));
    if (!selected) return;

    setTicketNo(selected.ticketno);

    try {
      const res = await getReceivedDocument(branchSchema, groupCode, selected.ticketno);
      setReceivedDocuments(res || []);
      if (isEmptyResult(res)) showToast('warn', NO_DATA_SUMMARY, NO_DATA_MESSAGE);
    } catch (err) {
      console.error('getReceivedDocument failed:', err);
      setReceivedDocuments([]);
      showToast('error', 'Load Failed', 'Failed to fetch received document details for this ticket.');
    }
  };

  const reasonTrimLen = reason.trim().length;
  const caoInvalid = touched && !cao;
  const groupInvalid = touched && !group;
  const ticketInvalid = touched && !ticket;
  const reasonInvalid = touched && (reasonTrimLen < 10 || reason.length > 200);

  const confirmDelete = () => {
    setTouched(true);

    if (reasonInvalid || reasonTrimLen < 10) {
      showToast('warn', 'Invalid Reason', 'Please enter a valid reason (min 10 characters).');
      return;
    }

    if (!ticketNo) {
      showToast('warn', 'No Ticket Selected', 'Please select a ticket first.');
      return;
    }

    if (receivedDocuments.length === 0) {
      showToast('warn', 'No Data Found', 'No received document records found for this ticket.');
      return;
    }

    const trimmedReason = reason.trim();

    const audit = {
      schemaName: branchSchema,
      loginName,
      loginTime: new Date().toISOString(),
      changeType: 'RECEIVED_DOCUMENT_KGMS_MVO_SVO_DELETE',
      oldData: {
        branchSchema,
        groupCode,
        ticketNo,
        chitGroupId: chitgroupId,
        fileDownloadStatus,
        receivedDocuments,
      },
      newData: {
        status: 'DELETED',
        fileDownloadStatus,
      },
      reason: trimmedReason,
    };

    setPendingAudit(audit);
    setPendingFileDownloadStatus(fileDownloadStatus);
    setPendingReason(trimmedReason);
    setShowConfirmModal(true);
  };

  const cancelConfirm = () => {
    if (saving) return;
    setShowConfirmModal(false);
  };

  const handleDeleteSuccess = () => {
    setReceivedDocuments([]);
    setSaving(false);
    setShowConfirmModal(false);
    showToast('success', 'Success', 'Audit saved and received document / surety details deleted successfully.', true);
    handleClear();
  };

  const doDelete = async () => {
    if (!pendingAudit) return;
    const audit = pendingAudit;

    setSaving(true);
    try {
      await saveData([audit]);

      try {
        await deleteKgmsMvoSvoDetails(
          audit.schemaName, audit.oldData.groupCode, audit.oldData.ticketNo, pendingFileDownloadStatus,
        );
        handleDeleteSuccess();
      } catch (err) {
        if (isSuccessResponse(err)) {
          handleDeleteSuccess();
        } else {
          setSaving(false);
          setShowConfirmModal(false);
          console.error('Delete failed:', err);
          showToast('warn', 'Partial Success', 'Audit saved, but delete failed. Please try again.', true);
        }
      }
    } catch (err) {
      setSaving(false);
      setShowConfirmModal(false);
      console.error('Audit save failed:', err);
      showToast('error', 'Audit Save Failed', 'Audit save failed. Delete was not performed.', true);
    }
  };

  const handleClear = () => {
    setFormKey((k) => k + 1);
    setCao('');
    resetFromBranch();
    setTouched(false);
  };

  const showRecordsSection = receivedDocuments.length > 0;
  const showEmptyState = ticket && receivedDocuments.length === 0;

  return (
    <div className="nc-page" style={{ position: 'relative' }}>
      <Toast toast={toast} onClose={() => setToast(null)} testId="received-document-toast" />

      <div style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '20px', position: 'relative' }} data-testid="received-document-form">
        {saving && (
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: '#002FA7', opacity: 0.6, borderRadius: '8px 8px 0 0' }} />
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
          <div style={{ width: '34px', height: '34px', borderRadius: '8px', background: '#EFF4FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <FileXls size={18} color="#002FA7" />
          </div>
          <div>
            <div style={{ fontSize: '11px', fontWeight: '600', color: '#002FA7', textTransform: 'uppercase' }}>Document Management</div>
            <div style={{ fontSize: '16px', fontWeight: '700', color: '#111827' }}>Received Document Not Show</div>
          </div>
        </div>

        {/* SECTION A: Ticket Selection */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>A</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Select Ticket</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Choose branch, group code and ticket to load received document records</div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              <Buildings size={13} /> CAO <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <SearchableSelect
              key={`cao-${formKey}`}
              options={branchList}
              getLabel={(o) => o.branch_name}
              getValue={(o) => o.branch_name}
              value={cao}
              onChange={handleBranchChange}
              placeholder="Select branch"
              emptyMessage="No branches found"
              error={caoInvalid}
              testId="rdns-cao-select"
            />
            {caoInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>CAO is required</div>}
          </div>

          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              <TreeStructure size={13} /> Group Code <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <SearchableSelect
              key={`group-${formKey}`}
              options={groupList}
              getLabel={(o) => o.group_Code}
              getValue={(o) => o.group_Code}
              value={group}
              onChange={handleGroupChange}
              placeholder="Select group code"
              emptyMessage="Select a CAO first"
              disabled={!branchSchema}
              error={groupInvalid}
              testId="rdns-group-select"
            />
            {groupInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Group Code is required</div>}
          </div>

          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              <Ticket size={13} /> Ticket No <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <SearchableSelect
              key={`ticket-${formKey}`}
              options={ticketList}
              getLabel={(o) => String(o.ticketno)}
              getValue={(o) => o.ticketno}
              value={ticket}
              onChange={handleTicketChange}
              placeholder="Select ticket"
              emptyMessage="Select a group code first"
              disabled={!groupCode}
              error={ticketInvalid}
              testId="rdns-ticket-select"
            />
            {ticketInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Ticket No is required</div>}
          </div>
        </div>

        {(showRecordsSection || ticket) && (
          <div style={{ height: '1px', background: '#E5E7EB', margin: '20px 0' }} />
        )}

        {/* SECTION B: Received Document Records */}
        {showRecordsSection && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>B</div>
              <div>
                <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Received Document Records</div>
                <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Surety document details fetched for the selected ticket</div>
              </div>
            </div>

            <div style={{ overflowX: 'auto', border: '1px solid #E5E7EB', borderRadius: '8px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ background: '#F9FAFB' }}>
                    <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: '11px', fontWeight: '600', color: '#6B7280' }}>Subscriber Name</th>
                    <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: '11px', fontWeight: '600', color: '#6B7280' }}>Filled Surety File</th>
                    <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: '11px', fontWeight: '600', color: '#6B7280' }}>SVO Filled Verified Document</th>
                  </tr>
                </thead>
                <tbody>
                  {receivedDocuments.map((row, idx) => (
                    <tr key={row.filled_surety_file ?? idx} style={{ borderTop: '1px solid #E5E7EB' }}>
                      <td style={{ padding: '8px 12px' }}>{row.subscriber_name || '—'}</td>
                      <td style={{ padding: '8px 12px', fontFamily: 'monospace' }}>{row.filled_surety_file || '—'}</td>
                      <td style={{ padding: '8px 12px', fontFamily: 'monospace' }}>{row.svofilledverfieddocument || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div
              onClick={() => setFileDownloadStatus((v) => !v)}
              style={{
                display: 'flex', alignItems: 'center', gap: '10px', marginTop: '16px', padding: '12px 14px',
                border: '1px solid #E5E7EB', borderRadius: '8px', background: fileDownloadStatus ? '#EFF4FF' : '#F9FAFB',
                cursor: 'pointer', userSelect: 'none',
              }}
              data-testid="rdns-filedownload-checkbox"
            >
              {fileDownloadStatus
                ? <CheckSquare size={20} color="#002FA7" weight="fill" />
                : <Square size={20} color="#9CA3AF" />}
              <span style={{ fontSize: '13px', color: '#111827' }}>Also reset file download status</span>
            </div>
          </>
        )}

        {/* Empty state */}
        {showEmptyState && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', padding: '24px', color: '#9CA3AF' }}>
            <Tray size={28} />
            <div style={{ fontSize: '13px' }}>No received document records found for the selected ticket.</div>
          </div>
        )}

        {/* SECTION C: Reason */}
        {showRecordsSection && (
          <>
            <div style={{ height: '1px', background: '#E5E7EB', margin: '20px 0' }} />

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
              <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>C</div>
              <div>
                <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Reason</div>
                <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Provide a reason for this deletion (min 10 characters)</div>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
              <label style={{ fontSize: '11px', fontWeight: '600', color: '#6B7280' }}>Reason <span style={{ color: '#DC2626' }}>*</span></label>
              <span style={{ fontSize: '11px', color: reasonTrimLen >= 10 ? '#059669' : reason.length > 180 ? '#D97706' : '#9CA3AF' }}>
                {reason.length} / 200
              </span>
            </div>
            <textarea
              className="input-field"
              style={{ fontSize: '13px', borderColor: reasonInvalid ? '#DC2626' : undefined }}
              rows={3}
              maxLength={200}
              placeholder="Enter reason (min 10 characters)..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              onKeyDown={(e) => preventExceedLength(e, 200)}
              data-testid="rdns-reason"
            />
            {reasonInvalid && (
              <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>
                {reasonTrimLen === 0 ? 'Reason is required.' : reasonTrimLen < 10 ? 'Minimum 10 characters required.' : 'Maximum 200 characters allowed.'}
              </div>
            )}
          </>
        )}

        {/* ACTION BAR */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#9CA3AF' }}>
            <Info size={13} /> Fields marked with <strong>*</strong> are required
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button type="button" onClick={handleClear} className="btn-secondary" disabled={saving} data-testid="rdns-clear-button">
              <ArrowClockwise size={14} style={{ marginRight: '4px' }} /> Clear
            </button>
            <button
              type="button"
              onClick={confirmDelete}
              className="btn-primary"
              style={{ background: '#DC2626', borderColor: '#DC2626' }}
              disabled={saving || receivedDocuments.length === 0}
              data-testid="rdns-delete-button"
            >
              <Trash size={14} style={{ marginRight: '4px' }} /> {saving ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </div>
      </div>

      {/* Confirm modal */}
      {showConfirmModal && (
        <div
          onClick={cancelConfirm}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.5)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 50,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: 'white', borderRadius: '10px', width: '420px', maxWidth: '90vw', overflow: 'hidden' }}
            data-testid="rdns-confirm-modal"
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #E5E7EB' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <WarningCircle size={18} color="#DC2626" />
                <h5 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#111827' }}>Confirm Deletion</h5>
              </div>
              <button type="button" onClick={cancelConfirm} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF' }}>
                <X size={16} />
              </button>
            </div>

            <div style={{ padding: '20px' }}>
              <p style={{ fontSize: '13px', color: '#6B7280', marginTop: 0 }}>
                Do you want to delete the received document / surety details for this ticket? This action cannot be undone.
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '14px', fontSize: '13px', color: '#111827' }}>
                <div><strong>Ticket No:</strong> {ticketNo}</div>
                <div><strong>Group Code:</strong> {groupCode}</div>
                <div><strong>Records:</strong> {receivedDocuments.length}</div>
                <div><strong>Reset File Download Status:</strong> {pendingFileDownloadStatus ? 'Yes' : 'No'}</div>
              </div>

              <div style={{ display: 'flex', gap: '8px', fontSize: '12px', color: '#6B7280', background: '#F9FAFB', padding: '10px', borderRadius: '6px' }}>
                <Info size={14} style={{ flexShrink: 0, marginTop: '2px' }} />
                <span>This action will be recorded in the audit log with the reason: &quot;{pendingReason}&quot;</span>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '14px 20px', borderTop: '1px solid #E5E7EB' }}>
              <button type="button" onClick={cancelConfirm} disabled={saving} className="btn-secondary" data-testid="rdns-confirm-cancel">
                Cancel
              </button>
              <button
                type="button"
                onClick={doDelete}
                disabled={saving}
                className="btn-primary"
                style={{ background: '#DC2626', borderColor: '#DC2626' }}
                data-testid="rdns-confirm-delete"
              >
                <Trash size={14} style={{ marginRight: '4px' }} /> {saving ? 'Deleting...' : 'Yes, Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ReceivedDocumentNotShowForm;